/**
 * Referral Utility Functions
 * Handles referral tracking from social sharing links
 */

/**
 * Extract referral parameters from URL
 * @returns {Object} Referral parameters if present
 */
const REF_STORAGE_KEY = "eg_share_ref";
const REF_TTL_MS = 60 * 60 * 1000; // 1 hour — a stored referral older than this is considered stale.

export const extractReferralParams = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const eventId = urlParams.get("eventId") || urlParams.get("eventid");
  const role = urlParams.get("role");
  // R44 (owner review 2026-07-20): the Share Generator's /s/:token landing page sends the clicker to
  // the reg link with `?ref=<shareToken>&utm_source=<platform>&utm_medium=social&utm_campaign=share`.
  // Only treat `ref`/`share` as a SHARE referral when it's OUR link — signalled by utm_medium=social /
  // utm_campaign=share — so a generic marketing `?ref=summer2026` doesn't poison attribution.
  const isOurShareLink = urlParams.get("utm_medium") === "social" || urlParams.get("utm_campaign") === "share";
  const refFromShare = isOurShareLink ? urlParams.get("ref") || urlParams.get("share") : null;
  const referUserId = urlParams.get("referral") || urlParams.get("refer") || refFromShare;
  const platform = urlParams.get("platform") || urlParams.get("utm_source");
  const name = urlParams.get("name");
  const email = urlParams.get("email");

  const hasReferralParams = !!referUserId;

  // PERSIST across navigation (landing → browse → register on another page) — with the eventId it was
  // captured for, so a stored referral can be SCOPED to that event (no cross-event misattribution).
  try {
    if (referUserId) {
      sessionStorage.setItem(
        REF_STORAGE_KEY,
        JSON.stringify({ referUserId, platform: platform || null, eventId: eventId || null, name: name || null, email: email || null, ts: Date.now() }),
      );
    }
  } catch { /* sessionStorage may be unavailable */ }

  return {
    eventId,
    role,
    referUserId,
    platform,
    name,
    email,
    hasReferralParams,
    originalLink: referUserId ? `${eventId || ""}-${role || ""}-${referUserId}-${platform || ""}` : null,
  };
};

/**
 * Read a previously-captured referral from sessionStorage. Only returns it if it's not stale (TTL) and
 * — when an `eventId` is given — was captured for that same event (prevents cross-event misattribution).
 */
export const getStoredReferral = (eventId) => {
  try {
    const raw = sessionStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || !o.referUserId) return null;
    if (o.ts && Date.now() - o.ts > REF_TTL_MS) { sessionStorage.removeItem(REF_STORAGE_KEY); return null; }
    // If we know the current event and the stored referral was for a DIFFERENT event, don't use it.
    if (eventId && o.eventId && String(o.eventId) !== String(eventId)) return null;
    return o;
  } catch {
    return null;
  }
};

/**
 * Track referral when page loads
 * @param {Object} referralParams - Referral parameters
 * @returns {Promise<boolean>} Success status
 */
export const trackReferral = async (referralParams) => {
  if (!referralParams.hasReferralParams) {
    return false;
  }

  try {
    console.log("🔍 Tracking referral:", referralParams);

    // Store referral data in sessionStorage for later use
    sessionStorage.setItem(
      "referral_data",
      JSON.stringify({
        ...referralParams,
        timestamp: Date.now(),
      })
    );

    // You can add additional tracking logic here
    // For example, sending analytics events, updating backend, etc.

    return true;
  } catch (error) {
    console.error("🚨 Referral tracking failed:", error);
    return false;
  }
};

/**
 * Get stored referral data
 * @returns {Object|null} Stored referral data
 */
export const getStoredReferralData = () => {
  try {
    const stored = sessionStorage.getItem("referral_data");
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("🚨 Error getting stored referral data:", error);
    return null;
  }
};

/**
 * Clear stored referral data
 */
export const clearStoredReferralData = () => {
  try {
    sessionStorage.removeItem("referral_data");
  } catch (error) {
    console.error("🚨 Error clearing stored referral data:", error);
  }
};

/**
 * Show referral success message
 * @param {Object} referralParams - Referral parameters
 * @param {Function} setSnackbar - Snackbar setter function
 */
export const showReferralMessage = (referralParams, setSnackbar) => {
  if (!referralParams.hasReferralParams) return;

  setSnackbar({
    open: true,
    message: `🎉 You were referred by someone! You'll get special benefits.`,
    severity: "success",
  });
};

/**
 * Prepare referral data for form submission
 * @param {Object} formData - Form data
 * @param {Object} referralParams - Referral parameters
 * @returns {Object} Enhanced form data with referral info
 */
export const prepareReferralFormData = (formData, referralParams, context = {}) => {
  // Fall back to a previously-captured referral (sessionStorage) if this page's URL has no ref params
  // — attribution must survive landing → browse → register on a different page.
  let refUserId = referralParams && referralParams.hasReferralParams ? referralParams.referUserId : null;
  let platform = referralParams && referralParams.platform;
  if (!refUserId) {
    // Scope the stored fallback to this page's event when known (context.eventId) — no cross-event bleed.
    const stored = getStoredReferral(context.eventId);
    if (stored) {
      refUserId = stored.referUserId;
      platform = stored.platform;
    }
  }
  if (!refUserId) return formData;

  // eventId/role now come from the REG PAGE's own context (the reg link resolved them). Only fall back
  // to the URL params (legacy links) when the page didn't supply them. processReferralAnalytics keys
  // sharing_insights.sharing_user_id on referUserId (= the share token), so eventId/role just scope it.
  const eventId = context.eventId || (referralParams && referralParams.eventId) || null;
  const role = context.role || (referralParams && referralParams.role) || null;

  return {
    ...formData,
    referralData: {
      eventId,
      role,
      referUserId: refUserId,
      platform: platform || null,
      originalLink: referralParams && referralParams.originalLink,
      timestamp: Date.now(),
    },
  };
};

/**
 * Check if current link should be modified for referral
 * @param {string} originalLink - Original registration link
 * @param {Object} referralParams - Referral parameters
 * @returns {string} Modified link with referral info
 */
export const getReferralLink = (originalLink, referralParams) => {
  if (!referralParams.hasReferralParams) {
    return originalLink;
  }

  // Return original link as referral tracking is handled by URL parameters
  return originalLink;
};

/**
 * Initialize referral tracking on component mount
 * @param {Function} setSnackbar - Snackbar setter function
 * @returns {Object} Referral parameters and tracking status
 */
export const initializeReferralTracking = (setSnackbar) => {
  const referralParams = extractReferralParams();

  if (referralParams.hasReferralParams) {
    trackReferral(referralParams);
    showReferralMessage(referralParams, setSnackbar);
  }

  return referralParams;
};

/**
 * R12 — the reg-link CHANNEL slug carried by `?source=<slug>`.
 *
 * Reception's "Registration Links" page mints a per-channel link whose URL is the role's normal
 * registration slug plus `?source=<reg_links.slug>` (e.g. `?source=ofsec-banner-6avq`). The API
 * already prefers that value when stamping `regLink` — its attribution ladder is
 * pmtLink → source → event_links.utmLink → bare slug — but this app never SENT it, so every
 * registration through a Reception channel link was attributed to the bare slug instead of the
 * channel. Observed live on OFSEC 2026: a visitor registered via `?source=ofsec-banner-6avq` was
 * stored as `ofsec-visitor-online-2026`, making the channel link impossible to measure.
 *
 * Returns null when absent, so a link without `?source` produces exactly the payload it always did.
 */
export const extractRegLinkSource = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get("source");
    const value = typeof raw === "string" ? raw.trim() : "";
    return value || null;
  } catch {
    return null; // non-browser context (SSR/tests) — attribution simply falls back
  }
};
